import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import WhatIsLifesum from '../../components/WhatIsLifesum/WhatIsLifesum'
import Review from '../../components/Review'
import check from 'media/check.svg'
import useBreakpoints from '../../hooks/useBreakpoints'
import convert from 'htmr'
import LazyImage from '../../components/LazyImage/LazyImage'
import QuotationMarks from '../../components/QuotationMarks'

export const getRecords = (section, type, single) => {
  return single
    ? section.find(({ __typename }) => __typename === type)
    : section.filter(({ __typename }) => __typename === type)
}

export default function Section({ section }) {
  const featureSectionCopyRecord = getRecords(
    section,
    'FeatureSectionCopyRecord',
    true,
  )
  const imageHeadingTextBlockRecord = getRecords(
    section,
    'ImageHeadingTextBlockRecord',
  )
  const iconAndTextRecords = getRecords(section, 'IconAndTextRecord')
  const squareImageRecord = getRecords(section, 'SquareImageRecord', true)
  const quoteBlockRecord = getRecords(section, 'QuoteBlockRecord', true)
  const checkmarkItemRecords = getRecords(section, 'CheckmarkItemRecord')
  const reviewRecord = getRecords(section, 'ReviewRecord', true)

  return (
    <div className="my-24 md:my-40 lg:my-72 xl:my-96">
      <div className="-mt-96 pb-96" />
      <div className="o-width-limiter text-center">
        <h2 className="type-large-fat mb-8">
          {featureSectionCopyRecord.heading}
        </h2>
        {featureSectionCopyRecord.intro && (
          <p className="demibolden-children mx-auto mb-24 max-w-576 leading-body md:mb-40 lg:max-w-768">
            {featureSectionCopyRecord.intro}
          </p>
        )}
      </div>
      {imageHeadingTextBlockRecord.length ? (
        <UspList items={imageHeadingTextBlockRecord} />
      ) : iconAndTextRecords.length ? (
        <SummaryList items={iconAndTextRecords} />
      ) : (
        <div className="o-width-limiter o-width-limiter--narrow">
          <div
            className={cn(
              squareImageRecord
                ? 'flex flex-col-reverse content-center lg:flex-row'
                : 'content-center lg:flex',
            )}
          >
            <div
              className={cn('lg:w-3/5 lg:pr-48 xl:pr-64', {
                'm-auto': squareImageRecord,
              })}
            >
              {checkmarkItemRecords.length && (
                <CheckmarkList items={checkmarkItemRecords} />
              )}
            </div>
            {squareImageRecord && (
              <div className="m-auto mb-32 w-2/3 shrink-0 lg:mb-auto lg:w-1/2">
                <LazyImage
                  className="rounded-16"
                  src={squareImageRecord.image.url}
                  width={squareImageRecord.image.width}
                  height={squareImageRecord.image.height}
                />
              </div>
            )}
            {quoteBlockRecord && (
              <div className="mt-12 text-center lg:w-2/5">
                <BlockQuote fields={quoteBlockRecord} />
              </div>
            )}
            {reviewRecord && (
              <div className="mt-12 text-center lg:w-2/5">
                <div className="rounded-8 bg-BgSub p-24 pb-2 lg:rounded-16 lg:p-32 lg:pb-8">
                  <Review
                    big
                    rating={reviewRecord.rating}
                    text={reviewRecord.reviewText}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

Section.propTypes = {
  section: PropTypes.array.isRequired,
}

function UspList({ items }) {
  const usps = items.map(({ image, heading, text }) => {
    return {
      image: image.url,
      imageDimensions: {
        width: image.width,
        height: image.height,
      },
      heading,
      text,
    }
  })
  return (
    <div className="o-width-limiter">
      <WhatIsLifesum content={usps} />
    </div>
  )
}

UspList.propTypes = {
  items: PropTypes.array.isRequired,
}

export function SummaryList({ items }) {
  return (
    <div className="o-width-limiter mt-24">
      <div className="mx-16 md:mx-auto">
        <div className="mx-auto max-w-768 leading-body lg:max-w-968">
          <ul className="md:columns-2 md:gap-x-12">
            {items.map(({ icon, text, id }) => (
              <li key={id} className="demibolden-children inline-flex w-full">
                <img
                  src={icon.url}
                  alt=""
                  className="mr-12 grow-0 self-start"
                />
                <div className="mb-16 md:mb-24">{convert(text)}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

SummaryList.propTypes = {
  items: PropTypes.array.isRequired,
}

function CheckmarkList({ items }) {
  const { LG } = useBreakpoints()
  return (
    <ul className="type-med-minus ml-24 mr-16 md:mx-auto">
      {items.map(({ text }, i) => (
        <li
          key={`check-${i}`}
          className="mx-auto max-w-480 py-12 pl-40 leading-body lg:max-w-none lg:pl-56"
          style={{
            background: `url(${check}) 0 ${LG ? 20 : 15}px no-repeat`,
            backgroundSize: LG ? '30px' : '25px',
          }}
        >
          {text}
        </li>
      ))}
    </ul>
  )
}

CheckmarkList.propTypes = {
  items: PropTypes.array.isRequired,
}

function BlockQuote({ fields: { portrait, quoteText, quotee, quoteeRole } }) {
  const { LG } = useBreakpoints()
  return (
    <blockquote>
      <p className="type-med-minus-fat leading-body text-BrandDark xxl:type-24-bold">
        <QuotationMarks>{quoteText}</QuotationMarks>
      </p>
      <footer>
        <cite>
          {portrait && (
            <img
              className="mb-12 mt-16 rounded-full md:mt-24"
              src={portrait.url}
              alt=""
              style={{ width: LG ? 75 : 60, height: LG ? 75 : 60 }}
            />
          )}
          <span className="type-small block">
            <span className="type-small-fat">{quotee}</span>
            {quoteeRole && (
              <>
                <span className="sr-only">, </span>
                <br />
                {quoteeRole}
              </>
            )}
          </span>
        </cite>
      </footer>
    </blockquote>
  )
}

BlockQuote.propTypes = {
  fields: PropTypes.shape({
    portrait: PropTypes.shape({
      url: PropTypes.string,
    }),
    quoteText: PropTypes.string.isRequired,
    quotee: PropTypes.string.isRequired,
    quoteeRole: PropTypes.string.isRequired,
  }),
}
